import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Typography,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";
import { formatPriceWithCurrency } from "../../utils/vat";

/**
 * Resurs Bank financing. Verstaani holds the Resurs agreement credentials and links the
 * workshop's Resurs store for it, so the workshop has no credentials to enter: it asks for
 * the service, then switches it on or off once the store is linked.
 */
function ResursFinancingCard({ t }) {
  const { currentOrganization, fetchUser } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  // "yes" = already a Resurs customer (ask Resurs to connect), "no" = new (lead to Resurs sales)
  const [agreement, setAgreement] = useState("");
  const [sendingContact, setSendingContact] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchState = async () => {
    try {
      const response = await axios.get(`/api/organizations/${organizationId}/resurs/`);
      setState(response.data);
      setLoadError(false);
    } catch (err) {
      // Show the card with an error instead of hiding it: a vanished card hides the reason.
      setState(null);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) fetchState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const toggle = async (enable) => {
    setSaving(true);
    try {
      const response = await axios.post(
        `/api/organizations/${organizationId}/resurs/${enable ? "activate" : "deactivate"}/`
      );
      setState({ ...state, ...response.data });
      setSnackbar({
        open: true,
        message: enable ? t("resurs.activated") : t("resurs.deactivated"),
        severity: "success",
      });
      fetchUser();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || t("Error"), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleSendContactRequest = async () => {
    setSendingContact(true);
    try {
      await axios.post(`/api/organizations/${organizationId}/resurs/request-contact/`, {
        has_agreement: agreement === "yes",
      });
      setSnackbar({ open: true, message: t("resurs.contactRequestSent"), severity: "success" });
      fetchState();
    } catch (err) {
      if (err.response?.status === 409) {
        setSnackbar({ open: true, message: t("resurs.contactRequestSent"), severity: "info" });
        fetchState();
      } else {
        setSnackbar({ open: true, message: err.response?.data?.error || t("Error"), severity: "error" });
      }
    } finally {
      setSendingContact(false);
    }
  };

  if (loading) return null;
  if (!state && !loadError) return null;

  const enabled = state?.resurs_financing_enabled;
  const linked = state?.linked;

  return (
    <>
      <Card variant="outlined" data-testid="resurs-settings-card">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "#ecfdf5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
                flexShrink: 0,
              }}
            >
              <AccountBalanceIcon sx={{ color: "#047857", fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t("resurs.settingsTitle")}
                </Typography>
                {linked && enabled && (
                  <Chip
                    label={t("resurs.connected")}
                    size="small"
                    color="success"
                    icon={<CheckCircleIcon />}
                    sx={{ height: 22, fontSize: "11px" }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t("resurs.settingsDescription")}
              </Typography>
            </Box>
          </Box>

          {loadError && (
            <Alert severity="error" sx={{ fontSize: "13px" }} data-testid="resurs-settings-error">
              {t("resurs.loadFailed")}
            </Alert>
          )}

          {linked && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {state.store_name && (
                <Typography variant="body2">
                  {t("resurs.store")}: <strong>{state.store_name}</strong>
                </Typography>
              )}
              {state.payment_methods?.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{t("resurs.paymentMethods")}:</Typography>
                  {state.payment_methods.map((method) => (
                    <Typography key={method.name} variant="body2" color="text.secondary">
                      {method.name} ({formatPriceWithCurrency(method.min_purchase)} - {formatPriceWithCurrency(method.max_purchase)})
                    </Typography>
                  ))}
                </Box>
              )}
              {state.payment_methods_error && (
                <Alert severity="warning" sx={{ fontSize: "13px" }}>{state.payment_methods_error}</Alert>
              )}
              <Box>
                {enabled ? (
                  <Button variant="outlined" color="error" size="small" onClick={() => toggle(false)} disabled={saving}>
                    {t("resurs.deactivate")}
                  </Button>
                ) : (
                  <Button variant="contained" size="small" onClick={() => toggle(true)} disabled={saving}>
                    {t("resurs.activate")}
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {state && !linked && (
            <>
              <Alert severity="info" sx={{ mb: 2, fontSize: "13px" }}>
                {t("resurs.requiresAgreement")}
              </Alert>
              {state.resurs_contact_requested_at ? (
                <Alert severity="success" sx={{ fontSize: "13px" }} data-testid="resurs-request-sent">
                  {t("resurs.contactRequestSentAt")}{" "}
                  {new Date(state.resurs_contact_requested_at).toLocaleDateString("fi-FI")}.{" "}
                  {state.resurs_contact_has_agreement === false ? t("resurs.requestedLead") : t("resurs.requestedConnect")}
                </Alert>
              ) : (
                <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <FormControl>
                    <FormLabel id="resurs-agreement-label" sx={{ fontSize: "14px", color: "text.primary" }}>
                      {t("resurs.agreementQuestion")}
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="resurs-agreement-label"
                      value={agreement}
                      onChange={(e) => setAgreement(e.target.value)}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio size="small" id="resurs-agreement-yes" />}
                        label={<Typography variant="body2">{t("resurs.agreementYes")}</Typography>}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio size="small" id="resurs-agreement-no" />}
                        label={<Typography variant="body2">{t("resurs.agreementNo")}</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>
                  {agreement && (
                    <Typography variant="body2" color="text.secondary">
                      {agreement === "yes" ? t("resurs.connectExplain") : t("resurs.leadExplain")}
                    </Typography>
                  )}
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={sendingContact ? <CircularProgress size={14} /> : <SendIcon />}
                      onClick={handleSendContactRequest}
                      disabled={sendingContact || !agreement}
                      data-testid="resurs-request-button"
                    >
                      {agreement === "no" ? t("resurs.sendLeadRequest") : t("resurs.sendConnectRequest")}
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default withTranslation()(ResursFinancingCard);
